import React from 'react';
import type {FetchEmploymentResponse} from '@rocket-ui/lk-api';
import {fetchEmployment, useFetchEmployment} from '@rocket-ui/lk-api';
import {useQueryClient} from 'react-query';
import {isSelfEmploymentProviderConnected} from './requiredSelfEmployment';

const EMPLOYMENT_QUERY_KEY = ['employment'] as const;
const EMPLOYMENT_SNAPSHOT_FRESH_MS = 60_000;

export type EmploymentGuardedAction = {
	openUrl?: string | null;
	run: () => void | Promise<void>;
};

function openInNewTab(uri: string) {
	window.open(uri, '_blank', 'noopener,noreferrer');
}

function openExternal(uri: string, popup: Window | null) {
	if (popup && !popup.closed) {
		try {
			popup.opener = null;
			popup.location.replace(uri);
			return;
		} catch {
			popup.close();
		}
	}

	openInNewTab(uri);
}

export function useRequiredSelfEmploymentGuard(provider: string | null | undefined) {
	const queryClient = useQueryClient();
	useFetchEmployment();
	const [isOpen, setIsOpen] = React.useState(false);
	const pendingRef = React.useRef(false);
	const close = React.useCallback(() => setIsOpen(false), []);

	const run = React.useCallback(
		async (action: EmploymentGuardedAction) => {
			const requiredProvider = provider?.trim() ?? '';

			if (!requiredProvider) {
				if (action.openUrl) openInNewTab(action.openUrl);
				await action.run();
				return;
			}

			if (pendingRef.current) return;
			pendingRef.current = true;

			try {
				const state = queryClient.getQueryState<FetchEmploymentResponse>(EMPLOYMENT_QUERY_KEY);
				const snapshot = state?.data;
				const isFresh =
					snapshot != null && Date.now() - (state?.dataUpdatedAt ?? 0) < EMPLOYMENT_SNAPSHOT_FRESH_MS;

				if (snapshot && isFresh) {
					if (!isSelfEmploymentProviderConnected(snapshot, requiredProvider)) {
						setIsOpen(true);
						return;
					}

					if (action.openUrl) openInNewTab(action.openUrl);
					await action.run();
					return;
				}

				const cachedConnected =
					snapshot != null && isSelfEmploymentProviderConnected(snapshot, requiredProvider);
				const popup =
					action.openUrl && (snapshot == null || cachedConnected) ? window.open('about:blank', '_blank') : null;

				let employment = snapshot;

				try {
					const {data} = await fetchEmployment();
					queryClient.setQueryData(EMPLOYMENT_QUERY_KEY, data);
					employment = data;
				} catch {
					employment = queryClient.getQueryData<FetchEmploymentResponse>(EMPLOYMENT_QUERY_KEY) ?? snapshot;
				}

				if (!employment || !isSelfEmploymentProviderConnected(employment, requiredProvider)) {
					popup?.close();
					setIsOpen(true);
					return;
				}

				if (action.openUrl) openExternal(action.openUrl, popup);
				await action.run();
			} finally {
				pendingRef.current = false;
			}
		},
		[provider, queryClient],
	);

	return {run, isOpen, close};
}
